/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
    Card,
    Checkbox,
    Col,
    Form,
    Input,
    Modal,
    notification,
    Radio,
    Row,
    Select,
    UploadFile
} from 'antd';
import { CheckboxOptionType } from 'antd/lib/checkbox';

import { ClientsField } from 'lib/ClientsField';
import { Authorization, PROFILE } from 'lib/helpers/Authorization.helper';
import { sleep } from 'lib/Sleep';
import { UploadField } from 'lib/UploadField';
import WhatsAppNumberField from 'lib/WhatsAppNumberField';
import { listAccountClients } from 'services/Account.service';
import { listNotifications } from 'services/Notification.service';

import Authentication from '../../lib/Authentication';
import { createUser, updateUser } from '../../services/UserService';
import { ClientInterface } from '../_Common/_Interfaces/Client';


type Props = {
    isOpen: boolean,
    closeForm: Function,
    user: User.Model | undefined
};

export type Values = {
    clientsToSync: number[],
    upload?: [UploadFile],
    name: User.Model['name'],
    email: User.Model['email'],
    phone: User.Model['phone'],
    profile: User.Model['profile'],
    picture: string
    can_only_view_assigned_issues: boolean,
    notifications_blocked_by_user?: number[]
};

const FormUser = (props: Props) => {
    const history = useHistory();
    const [form] = Form.useForm<Values>();
    const [isSaving, setSaving] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [clients, setClients] = useState<Array<{
        value: number,
        label: string
    }>>([]);
    const [clientsCurrent, setClientsCurrent] = useState<number[]>([]);
    const [notifications, setNotifications] = useState<CheckboxOptionType<number>[]>([]);
    const [selectedProfile, setSelectedProfile] = useState<User.Profile | undefined>(undefined);

    async function onFinish() {
        try {
            setSaving(true);

            const user = await form.validateFields();

            let picture = null;

            if (user.upload !== undefined) {
                const upload: UploadFile = form.getFieldValue('upload')[0];
                picture = upload.response ? upload.response : upload.url;
                delete user.upload;
            }

            const notifications_allowed_by_user = user.notifications_blocked_by_user;
            delete user.notifications_blocked_by_user;

            const notifications_blocked_by_user = notifications.filter(n => !notifications_allowed_by_user?.includes(n.value)).map(n => n.value);

            const response = props.user?.id
                ? await updateUser(props.user?.id, { ...user, picture, notifications_blocked_by_user })
                : await createUser({ ...user, picture, notifications_blocked_by_user });

            notification.open(response);
            setSaving(false);
            props.closeForm();

            await sleep(1000);

            if (props.user?.id === Authentication.getUserId()) {
                notification.info({
                    message: 'Atenção',
                    description: 'Estamos redirecionando você para página de login... Para que seu usuário seja atualizado, você deve realizar novamente seu acesso.',
                    placement: 'top',
                    duration: 5
                });

                await sleep(3000);

                history.push('/login');
                Authentication.logout();
            }

        } catch (error) {
            setSaving(false);
        }
    };

    const fetchClient = async () => {
        setLoading(true);
        const response = await listAccountClients();

        const allowedClientsIds = Authentication.getClients().map(client => client.id);

        if (response.success) {

            const clientsOption = response.clients.map(({ id, name }) => ({
                value: id,
                label: name,
                disabled: !allowedClientsIds.includes(id)
            }));

            setClients(clientsOption);
        }

        const currentClients = props.user
            ? props.user.clients.map((c: ClientInterface) => c.id)
            : allowedClientsIds;

        setClientsCurrent(currentClients);
        setLoading(false);
    };

    const fetchNotifications = async () => {
        const response = await listNotifications();

        if (!response.success)
            return;

        const data: CheckboxOptionType<number>[] = response.notifications
            .filter(n => n.sending_platform === 'email'
            ).map(n => ({
                label: n.title,
                value: n.id
            }));

        setNotifications(data);
    };

    useEffect(() => {
        if (props.user !== undefined) {
            const file: UploadFile = {
                uid: props.user.id.toString(),
                name: '',
                url: props.user.picture,
                status: 'done',
            };

            form.setFieldsValue({
                clientsToSync: clientsCurrent,
                upload: [file]
            });

            validateNotificationsCheckbox();
        }

        fetchClient();
        fetchNotifications();

    }, [props.user]);

    const validateNotificationsCheckbox = () => {
        const result = notifications
            .filter(n => !props.user?.notifications_blocked.find(nb => nb.id === n.value))
            .map(n => n.value);

        form.setFieldValue('notifications_blocked_by_user', result);
    };

    const profileDescriptions: Record<User.Profile, string[]> = {
        admin: [
            'Acesso total a todos os módulos.'
        ],
        concierge: [
            'Acesso às unidades e aos dados dos moradores (somente listagem, sem detalhes).',
            'Acesso ao módulo de veículos.',
            'Visualiza o calendário com ambientes reservados.',
            'Pode abrir chamados, mas não consegue visualizar chamados.'
        ],
        caretaker: [
            'Acesso quase total aos módulos.',
            'Sem acesso aos módulos: usuários, documentos, fornecedores, configuração de ambientes, chatbots e condôminos.'
        ],
        measurement: [
            'Acesso somente ao módulo de consumos.'
        ],
        manager: [
            'Sem acesso à página "Minha conta".',
            'Sem acesso aos módulos: Usuários e Controle de férias.'
        ],
        issue: [
            'Gerencia apenas os chamados.'
        ],
        issue_maintenance: [
            'Gerencia apenas os chamados e manutenções.'
        ],
        advisor: [
            'Acesso somente de visualização (sem criar, editar ou excluir).',
            'Pode emitir relatórios.',
            'Tem acesso aos módulos: chamados, manutenções, obras, cronograma, relatórios e dashboard.'
        ]
    };

    return (
        <Modal
            width={900}
            centered
            title={props.user?.id ? 'Editar usuário' : 'Novo usuário'}
            destroyOnClose={true}
            open={props.isOpen}
            confirmLoading={isSaving}
            onOk={onFinish}
            onCancel={() => props.closeForm()}
            afterClose={() => form.resetFields()}
            okText="Salvar"
        >
            <Form form={form} layout="vertical" initialValues={props.user} onFinish={onFinish}>
                
                <UploadField
                    name="upload"
                    label="Foto"
                    type="picture-card"
                    multiple={false}
                    maxCount={1}
                />

                <Form.Item
                    name="name"
                    label="Nome"
                    rules={[{ required: true, message: 'Por favor, insira um nome.' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item name="email"
                    label="Email"
                    rules={[{ required: true, message: 'Por favor, insira um e-mail.' }]}
                >
                    <Input />
                </Form.Item>

                <WhatsAppNumberField name='phone' />

                <Form.Item
                    name="profile"
                    label="Perfil"
                    rules={[{ required: true, message: 'Por favor, selecione um perfil.' }]}
                >
                    <Select onChange={(value: User.Profile) => setSelectedProfile(value)}>
                        {Object.values(PROFILE).map(profile => (
                            <Select.Option value={profile} key={profile}>
                                {Authorization.translateProfile(profile)}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                {selectedProfile && profileDescriptions[selectedProfile] && (
                    <Form.Item>
                        <Card
                            size="small"
                            type="inner"
                            title="Permissões do perfil selecionado"
                            style={{
                                backgroundColor: '#fafafa',
                                border: '1px solid #e8e8e8',
                                borderRadius: 8,
                                marginTop: 8,
                            }}
                        >
                            <ul style={{ paddingLeft: 20, margin: 0 }}>
                                {profileDescriptions[selectedProfile].map((desc, index) => (
                                    <li key={index}>{desc}</li>
                                ))}
                            </ul>
                        </Card>
                    </Form.Item>
                )}

                <ClientsField<Values>
                    name="clientsToSync"
                    label="Condomínios que o usuário deve ter acesso"
                    tooltip="Os condomínios listados são administrados por você. Caso queira, poderá liberar acesso a todos eles para o usuário."
                    rules={[{ required: true, message: 'Por favor, selecione ao menos um condomínio.' }]}
                    disabled={isLoading}
                    loading={isLoading}
                    options={clients}
                    form={form}
                />

                <Row>
                    <Col span={24}>
                        <Form.Item
                            name="can_only_view_assigned_issues"
                            label="Deve visualizar apenas chamados pelo qual é responsável?"
                            rules={[{ required: true, message: 'Por favor, selecione uma opção.' }]}
                        >
                            <Radio.Group>
                                <Radio value={false}>Não</Radio>
                                <Radio value={true}>Sim</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Form.Item name='notifications_blocked_by_user' label='Quais notificações por email o usuário deseja receber?'>
                            <Checkbox.Group style={{ width: '100%' }}>
                                <Row>
                                    {notifications.map(notification => (
                                        <Col span={12}>
                                            <Checkbox value={notification.value}>
                                                {notification.label}
                                            </Checkbox>
                                        </Col>
                                    ))}
                                </Row>
                            </Checkbox.Group>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal >
    );

};

export default FormUser;